import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import {
  MEDIA_INPUTS_SECTIONS,
  REVIEW_UNIT_TITLES,
  ReviewUnit,
  SECTION_TITLES,
  SectionKeyType,
  SectionStatus,
  SectionStatusType
} from './data-qa-constants';
import {
  ChannelConfig,
  ChannelStats,
  DataQaDataset,
  FeatureMappingRow,
  MmmConfig,
  NegativeValueFinding,
  PortfolioStats,
  WeekChannelTotals,
  WeekRow
} from './data-qa-types';
import { filterWeeksToFencepost } from './data-qa-utils';
import { computeDivergentRanges, DivergenceRange } from './divergence-ranges';
import { isBlockingFinding, periodUnitFor } from './negative-values-utils';

// --- Review status across Media Inputs' two persisted sections -----------------------

// Media Inputs writes one decision to both Portfolio and Channels, but older reviews (or
// an inherited copy) can leave them apart: a flag on either wins, both must be approved
// to count as approved, anything else is unreviewed.
export const combineSectionStatus = (statuses: SectionStatusType[]): SectionStatusType => {
  if (statuses.some(s => s === SectionStatus.FLAGGED)) return SectionStatus.FLAGGED;
  if (statuses.length > 0 && statuses.every(s => s === SectionStatus.APPROVED)) {
    return SectionStatus.APPROVED;
  }
  return SectionStatus.UNREVIEWED;
};

// "Media Inputs" for its two sections together, else the sections' own titles.
export const reviewTitle = (sections: SectionKeyType[]): string =>
  sections.length === MEDIA_INPUTS_SECTIONS.length &&
  MEDIA_INPUTS_SECTIONS.every(s => sections.includes(s))
    ? REVIEW_UNIT_TITLES[ReviewUnit.MEDIA_INPUTS]
    : sections.map(s => SECTION_TITLES[s]).join(' and ');

// Distinct non-empty notes, in section order.
export const combineSectionNotes = (notes: string[]): string =>
  [...new Set(notes.map(n => n.trim()).filter(Boolean))].join('\n\n');

// --- Chart analysis shared by the charts and their pill indicators -------------------

// Adds each requested platform's spend/impressions/bpmSpend/bpmImpressions to the row as
// `${metric}__${platformKey}` fields, for the per-platform chart series to read.
export const flattenChannel = (
  channelKey: string,
  weeks: WeekRow[],
  platformKeys: string[] = []
): WeekRow[] =>
  weeks.map(w => {
    const perPlatform = w.perChannel[channelKey]?.perPlatform ?? {};
    const platformFields: Record<string, number | undefined> = {};
    platformKeys.forEach(platformKey => {
      platformFields[`spend__${platformKey}`] = perPlatform[platformKey]?.spend ?? 0;
      platformFields[`impressions__${platformKey}`] = perPlatform[platformKey]?.impressions ?? 0;
      platformFields[`bpmSpend__${platformKey}`] = perPlatform[platformKey]?.bpmSpend;
      platformFields[`bpmImpressions__${platformKey}`] = perPlatform[platformKey]?.bpmImpressions;
    });
    return {
      ...w,
      spend: w.perChannel[channelKey]?.spend ?? 0,
      impressions: w.perChannel[channelKey]?.impressions ?? 0,
      bpmSpend: w.perChannel[channelKey]?.bpmSpend,
      bpmImpressions: w.perChannel[channelKey]?.bpmImpressions,
      ...platformFields
    };
  });

// The one typed accessor for flattenChannel's dynamic per-platform fields.
export const platformField = (week: WeekRow, field: string): number | undefined =>
  (week as unknown as Record<string, number | undefined>)[field];

export interface FencepostWindow {
  fencepostStart?: string;
  fencepostEnd?: string;
  reviewGranularity?: string;
}

// One charted series' divergent ranges against BPM, for the indicator's reasons: a metric,
// for one platform once the channel is broken out by platform.
export interface DivergentSeries {
  metric: 'spend' | 'impressions';
  platform?: string;
  ranges: DivergenceRange[];
}

export interface ChannelAnalysis {
  chartWeeks: WeekRow[];
  // The model's period in words ("week"), for counting divergent periods.
  periodUnit: string;
  activePlatforms: { key: string; label: string }[];
  hasPlatformBreakout: boolean;
  platformKeys: string[];
  hasImpressions: boolean;
  corrStrong: boolean;
  hasBpm: boolean;
  // Exactly what the charts shade: per platform once broken out, else channel totals.
  spendRanges: DivergenceRange[];
  impressionsRanges: DivergenceRange[];
  // The same ranges, per series.
  divergentSeries: DivergentSeries[];
  // Delivery issues shown under the charts (zero-spend weeks, weak correlation).
  issues: string[];
  correlation: number;
  // Weak spend-vs-impressions correlation -- the one delivery issue that feeds the pill
  // indicator. Zero-spend weeks stay on the chart only (too common to triage on).
  lowCorrelation: boolean;
}

const sortRanges = (ranges: DivergenceRange[], weeks: WeekRow[]): DivergenceRange[] => {
  const weekIndex = new Map(weeks.map((w, i) => [w.weekLabel, i]));
  return [...ranges].sort((a, b) => (weekIndex.get(a.from) ?? 0) - (weekIndex.get(b.from) ?? 0));
};

// One metric's divergent ranges against BPM, week by week, from a per-week totals
// accessor: a channel's, or one of its platforms'. The one place a series is compared, so a
// channel's indicator and About's platform rows apply the same rule.
export const metricRanges = (
  weeks: WeekRow[],
  totalsOf: (w: WeekRow) => WeekChannelTotals | undefined,
  metric: DivergentSeries['metric']
): DivergenceRange[] => {
  const bpmMetric = metric === 'spend' ? 'bpmSpend' : 'bpmImpressions';
  return computeDivergentRanges(
    weeks.map(w => ({
      xValue: w.weekLabel,
      mmm: totalsOf(w)?.[metric] ?? 0,
      bpm: totalsOf(w)?.[bpmMetric]
    }))
  );
};

// Everything a channel's charts show, computed once so its pill indicator can never
// disagree with them. Charts break out by platform only when more than one platform has
// any nonzero delivery in the window -- a channel-level total can mask a platform-level
// mismatch, so each platform is then compared with its own BPM baseline.
export const analyzeChannel = (
  channel: ChannelConfig,
  weeks: WeekRow[],
  stats: ChannelStats | undefined,
  { fencepostStart, fencepostEnd, reviewGranularity }: FencepostWindow
): ChannelAnalysis => {
  const allPlatformKeys = channel.platforms.map(p => p.key);
  const chartWeeks = filterWeeksToFencepost(
    flattenChannel(channel.key, weeks, allPlatformKeys),
    fencepostStart,
    fencepostEnd,
    reviewGranularity
  );
  const activePlatforms = channel.platforms.filter(p =>
    chartWeeks.some(
      w =>
        (platformField(w, `spend__${p.key}`) ?? 0) !== 0 ||
        (platformField(w, `impressions__${p.key}`) ?? 0) !== 0
    )
  );
  const hasPlatformBreakout = activePlatforms.length > 1;
  const platformKeys = hasPlatformBreakout ? activePlatforms.map(p => p.key) : [];
  const hasImpressions = (stats?.avgImpressions ?? 0) > 0;
  const corrStrong = (stats?.platformCorrelation ?? 0) > 0.7;
  const hasBpm = weeks.some(w => w.perChannel[channel.key]?.bpmSpend !== undefined);

  const channelTotals = (w: WeekRow) => w.perChannel[channel.key];
  const seriesFor = (metric: DivergentSeries['metric']): DivergentSeries[] => {
    if (!hasBpm) return [];
    if (!hasPlatformBreakout) {
      return [{ metric, ranges: metricRanges(chartWeeks, channelTotals, metric) }];
    }
    return activePlatforms.map(platform => ({
      metric,
      platform: platform.label,
      ranges: metricRanges(chartWeeks, w => channelTotals(w)?.perPlatform?.[platform.key], metric)
    }));
  };
  const spendSeries = seriesFor('spend');
  // Impressions ranges only matter when the impressions chart is shown.
  const impressionsSeries = hasImpressions ? seriesFor('impressions') : [];
  const flatRanges = (series: DivergentSeries[]) =>
    sortRanges(
      series.flatMap(x => x.ranges),
      chartWeeks
    );

  const issues: string[] = [];
  if (stats && stats.zeroValueWeeks > 0) {
    issues.push(
      `${stats.zeroValueWeeks} week${stats.zeroValueWeeks === 1 ? '' : 's'} with zero spend`
    );
  }
  if (hasImpressions && !corrStrong) {
    issues.push('Low spend-vs-impressions correlation');
  }

  return {
    chartWeeks,
    periodUnit: periodUnitFor(reviewGranularity),
    activePlatforms,
    hasPlatformBreakout,
    platformKeys,
    hasImpressions,
    corrStrong,
    hasBpm,
    spendRanges: flatRanges(spendSeries),
    impressionsRanges: flatRanges(impressionsSeries),
    divergentSeries: [...spendSeries, ...impressionsSeries].filter(x => x.ranges.length > 0),
    issues,
    correlation: stats?.platformCorrelation ?? 0,
    lowCorrelation: hasImpressions && !corrStrong
  };
};

export interface PortfolioAnalysis {
  chartWeeks: WeekRow[];
  periodUnit: string;
  hasBpm: boolean;
  spendRanges: DivergenceRange[];
  impressionsRanges: DivergenceRange[];
  correlation: number;
  corrStrong: boolean;
  hasUnresolvedMedia: boolean;
}

export const analyzePortfolio = (
  weeks: WeekRow[],
  portfolioStats: PortfolioStats,
  { fencepostStart, fencepostEnd, reviewGranularity }: FencepostWindow
): PortfolioAnalysis => {
  const chartWeeks = filterWeeksToFencepost(weeks, fencepostStart, fencepostEnd, reviewGranularity);
  const hasBpm = weeks.some(w => w.bpmSpend !== undefined);
  return {
    chartWeeks,
    periodUnit: periodUnitFor(reviewGranularity),
    hasBpm,
    spendRanges: hasBpm
      ? computeDivergentRanges(
          chartWeeks.map(w => ({ xValue: w.weekLabel, mmm: w.spend, bpm: w.bpmSpend }))
        )
      : [],
    impressionsRanges: hasBpm
      ? computeDivergentRanges(
          chartWeeks.map(w => ({ xValue: w.weekLabel, mmm: w.impressions, bpm: w.bpmImpressions }))
        )
      : [],
    correlation: portfolioStats.correlation,
    corrStrong: portfolioStats.correlation > 0.7,
    hasUnresolvedMedia:
      portfolioStats.unresolvedSpend > 0 || portfolioStats.unresolvedImpressions > 0
  };
};

// --- Pill indicators ----------------------------------------------------------------

// Worst first. 'noBpm': nothing flagged, but there's no BPM baseline to compare against,
// so a clean result isn't a confirmed one.
export const ReviewIndicator = {
  INVESTIGATE: 'investigate',
  NOTE: 'note',
  NO_BPM: 'noBpm',
  CLEAR: 'clear'
} as const;
export type ReviewIndicatorType = (typeof ReviewIndicator)[keyof typeof ReviewIndicator];

const INDICATOR_RANK: Record<ReviewIndicatorType, number> = {
  [ReviewIndicator.INVESTIGATE]: 3,
  [ReviewIndicator.NOTE]: 2,
  [ReviewIndicator.NO_BPM]: 1,
  [ReviewIndicator.CLEAR]: 0
};

export const worstIndicator = (indicators: ReviewIndicatorType[]): ReviewIndicatorType =>
  indicators.reduce<ReviewIndicatorType>(
    (worst, next) => (INDICATOR_RANK[next] > INDICATOR_RANK[worst] ? next : worst),
    ReviewIndicator.CLEAR
  );

// Why an indicator is the color it is, e.g. "Low spend-vs-impressions correlation (0.52)",
// for its hover text. An indicator is the worst of its reasons' (CLEAR when there are none).
export interface IndicatorReason {
  indicator: ReviewIndicatorType;
  text: string;
}

const METRIC_LABEL = { spend: 'Spend', impressions: 'Impressions' } as const;

const SEVERITY_REASON = [
  { severity: 'investigate', indicator: ReviewIndicator.INVESTIGATE, offBy: 'more than 1%' },
  { severity: 'note', indicator: ReviewIndicator.NOTE, offBy: '0.5–1%' }
] as const;

// How many periods any of these ranges cover (overlapping platforms count once).
const periodsCovered = (ranges: DivergenceRange[], weeks: WeekRow[]): number => {
  const weekIndex = new Map(weeks.map((w, i) => [w.weekLabel, i]));
  const covered = new Set<number>();
  ranges.forEach(r => {
    for (let i = weekIndex.get(r.from) ?? 0; i <= (weekIndex.get(r.to) ?? -1); i += 1) {
      covered.add(i);
    }
  });
  return covered.size;
};

// One reason per metric and severity, across platforms: "Spend more than 1% off BPM in
// 3 weeks (Meta, TikTok)", "Impressions 0.5–1% off BPM in 2 weeks".
export const divergenceReasons = (
  series: DivergentSeries[],
  weeks: WeekRow[],
  periodUnit: string
): IndicatorReason[] =>
  SEVERITY_REASON.flatMap(({ severity, indicator, offBy }) =>
    (['spend', 'impressions'] as const).flatMap(metric => {
      const matching = series
        .filter(x => x.metric === metric)
        .map(x => ({ ...x, ranges: x.ranges.filter(r => r.severity === severity) }))
        .filter(x => x.ranges.length > 0);
      if (matching.length === 0) return [];
      const periods = periodsCovered(
        matching.flatMap(x => x.ranges),
        weeks
      );
      const platforms = matching.flatMap(x => (x.platform ? [x.platform] : []));
      const where = platforms.length > 0 ? ` (${platforms.join(', ')})` : '';
      return [
        {
          indicator,
          text: `${METRIC_LABEL[metric]} ${offBy} off BPM in ${conditionallyPluralizeWithCount(periods, periodUnit)}${where}`
        }
      ];
    })
  );

const lowCorrelationReason = (correlation: number): IndicatorReason => ({
  indicator: ReviewIndicator.NOTE,
  text: `Low spend-vs-impressions correlation (${correlation.toFixed(2)})`
});

const NO_BPM_REASON: IndicatorReason = {
  indicator: ReviewIndicator.NO_BPM,
  text: 'No BPM data to compare against'
};

// Negative values: blocking -> investigate, warning -> note. Worded like the tab's alerts.
const negativeReasons = (findings: NegativeValueFinding[]): IndicatorReason[] => {
  const reasons: IndicatorReason[] = [];
  if (findings.some(isBlockingFinding)) {
    reasons.push({
      indicator: ReviewIndicator.INVESTIGATE,
      text: 'Negative values will break model training'
    });
  }
  if (findings.some(f => !isBlockingFinding(f))) {
    reasons.push({ indicator: ReviewIndicator.NOTE, text: 'Negative values to check' });
  }
  return reasons;
};

// Worst first; the sort is stable, so reasons of one severity keep their order.
export const sortReasons = (reasons: IndicatorReason[]): IndicatorReason[] =>
  [...reasons].sort((a, b) => INDICATOR_RANK[b.indicator] - INDICATOR_RANK[a.indicator]);

export const reasonsIndicator = (reasons: IndicatorReason[]): ReviewIndicatorType =>
  worstIndicator(reasons.map(r => r.indicator));

export const channelReasons = (
  analysis: ChannelAnalysis,
  negativeFindings: NegativeValueFinding[] = []
): IndicatorReason[] =>
  sortReasons([
    ...divergenceReasons(analysis.divergentSeries, analysis.chartWeeks, analysis.periodUnit),
    ...(analysis.lowCorrelation ? [lowCorrelationReason(analysis.correlation)] : []),
    ...negativeReasons(negativeFindings),
    ...(analysis.hasBpm ? [] : [NO_BPM_REASON])
  ]);

export const portfolioReasons = (
  analysis: PortfolioAnalysis,
  negativeFindings: NegativeValueFinding[] = []
): IndicatorReason[] =>
  sortReasons([
    ...divergenceReasons(
      [
        { metric: 'spend', ranges: analysis.spendRanges },
        { metric: 'impressions', ranges: analysis.impressionsRanges }
      ],
      analysis.chartWeeks,
      analysis.periodUnit
    ),
    ...(analysis.corrStrong ? [] : [lowCorrelationReason(analysis.correlation)]),
    ...(analysis.hasUnresolvedMedia
      ? [
          {
            indicator: ReviewIndicator.NOTE,
            text: 'Spend or impressions with no channel/platform mapping'
          }
        ]
      : []),
    ...negativeReasons(negativeFindings),
    ...(analysis.hasBpm ? [] : [NO_BPM_REASON])
  ]);

// A split channel's pill or summary carries both entries' reasons, each prefixed with its
// side ("DG: ...").
export const prefixReasons = (reasons: IndicatorReason[], prefix: string): IndicatorReason[] =>
  reasons.map(r => ({ ...r, text: `${prefix}: ${r.text}` }));

// The reasons worth showing under an indicator's label: the ones that set its color, so a
// red dot says what's red without the yellow detail the tab shows anyway. "No BPM data"
// alone only repeats the label's own "No BPM comparison".
export const reasonTexts = (reasons: IndicatorReason[]): string[] => {
  const indicator = reasonsIndicator(reasons);
  if (indicator === ReviewIndicator.NO_BPM) return [];
  return reasons.filter(r => r.indicator === indicator).map(r => r.text);
};

const FEATID_IN_COLUMN = /^(featid\w+?)__/i;

// Feature-level ('media') negative-value findings, keyed by the ChannelConfig key they
// belong to: column featid -> featureMapping row (whose channel is the ChannelConfig's
// rawLabel, plus its demand group) -> that channel entry. Findings that resolve to no
// channel are dropped -- they still show in the page-level alert.
export const negativeFindingsByChannel = (
  findings: NegativeValueFinding[],
  featureMapping: FeatureMappingRow[],
  channels: ChannelConfig[]
): Map<string, NegativeValueFinding[]> => {
  const byChannel = new Map<string, NegativeValueFinding[]>();
  findings
    .filter(f => f.kind === 'media')
    .forEach(finding => {
      const featid = FEATID_IN_COLUMN.exec(finding.column)?.[1]?.toUpperCase();
      if (!featid) return;
      const row = featureMapping.find(r =>
        r.featureId
          .split(',')
          .map(id => id.trim().toUpperCase())
          .includes(featid)
      );
      const channel = row
        ? channels.find(c => c.rawLabel === row.channel && c.group === row.group)
        : undefined;
      if (!channel) return;
      byChannel.set(channel.key, [...(byChannel.get(channel.key) ?? []), finding]);
    });
  return byChannel;
};

export interface ChannelView {
  analysis: ChannelAnalysis;
  indicator: ReviewIndicatorType;
  reasons: IndicatorReason[];
}

// Each channel entry's analysis (what its Media Inputs charts show) and the indicator and
// reasons derived from it, keyed by ChannelConfig key -- built together so a pill, tab or
// summary row can never lack one.
export const buildChannelViews = (
  config: Pick<MmmConfig, 'channels' | 'featureMapping'>,
  dataset: Pick<DataQaDataset, 'weeks' | 'channelStats' | 'negativeValues'>,
  window: FencepostWindow
): Map<string, ChannelView> => {
  const negativeByChannel = negativeFindingsByChannel(
    dataset.negativeValues ?? [],
    config.featureMapping,
    config.channels
  );
  return new Map(
    config.channels.map(channel => {
      const analysis = analyzeChannel(
        channel,
        dataset.weeks,
        dataset.channelStats.find(c => c.channelKey === channel.key),
        window
      );
      const reasons = channelReasons(analysis, negativeByChannel.get(channel.key) ?? []);
      return [channel.key, { analysis, indicator: reasonsIndicator(reasons), reasons }];
    })
  );
};

// --- Pills ----------------------------------------------------------------------------

export interface ChannelPill {
  // The channel's name without any demand-group suffix (ChannelConfig.rawLabel).
  label: string;
  // One entry, or two for a channel split across Demand Generation and Demand Capture
  // (Demand Generation first).
  entries: ChannelConfig[];
}

// One pill per channel, in config.channels' order of first appearance.
export const buildChannelPills = (channels: ChannelConfig[]): ChannelPill[] => {
  const pills: ChannelPill[] = [];
  channels.forEach(channel => {
    const pill = pills.find(p => p.label === channel.rawLabel);
    if (pill) {
      pill.entries.push(channel);
    } else {
      pills.push({ label: channel.rawLabel, entries: [channel] });
    }
  });
  const groupOrder = (channel: ChannelConfig) => (channel.group === 'demandGeneration' ? 0 : 1);
  pills.forEach(pill => pill.entries.sort((a, b) => groupOrder(a) - groupOrder(b)));
  return pills;
};

// --- Cross-model banners for a review unit ---------------------------------------------

// A review unit reads as inherited from another model only when every one of its sections
// was, with the same status. Returns the last section's record (its source label/notes).
export const combineInherited = <T extends { status: SectionStatusType }>(
  records: (T | undefined)[]
): T | undefined => {
  const defined = records.filter((r): r is T => r !== undefined);
  if (defined.length === 0 || defined.length !== records.length) return undefined;
  const [first] = defined;
  return defined.every(r => r.status === first.status) ? defined[defined.length - 1] : undefined;
};

// A review unit's data is identical to another model's only where every one of its
// sections matches -- the intersection of their matching models.
export const combineInvariance = <T extends { checking: boolean; matchingModelLabels: string[] }>(
  records: (T | undefined)[]
): { checking: boolean; matchingModelLabels: string[] } | undefined => {
  const defined = records.filter((r): r is T => r !== undefined);
  if (defined.length === 0 || defined.length !== records.length) return undefined;
  const [first, ...rest] = defined;
  return {
    checking: defined.some(r => r.checking),
    matchingModelLabels: first.matchingModelLabels.filter(label =>
      rest.every(r => r.matchingModelLabels.includes(label))
    )
  };
};
